import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { 
  Visibility as ViewIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import MagicSelect from '../ui/FormFields/MagicSelect';
import { getMyTasksViewableUsers } from '../../services/taskService';
import useAuthStore from '../../stores/authStore';
import useNotificationStore from '../../stores/notificationStore';

const ViewAsSelector = ({ 
  currentViewAsUser, 
  onViewAsChange, 
  canManage = false,
  sx = {} 
}) => {
  const [viewableUsers, setViewableUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user: currentUser } = useAuthStore();
  const { showInfo, showError } = useNotificationStore();

  useEffect(() => {
    fetchViewableUsers();
  }, []);

  const fetchViewableUsers = async () => {
    setLoading(true);
    try {
      const response = await getMyTasksViewableUsers();
      if (response.status === 'success') {
        setViewableUsers(response.data?.users || []);
      }
    } catch (error) {
      console.error('Failed to fetch viewable users:', error);
      showError('Failed to load viewable users');
    } finally {
      setLoading(false);
    }
  };

  const handleViewAsChange = (userId) => {
    onViewAsChange(userId === 'default' ? null : userId);
    
    if (userId === 'default') {
      showInfo('Switched to your own tasks');
    } else {
      const selectedUser = viewableUsers.find(u => u._id === userId);
      if (selectedUser) {
        const userName = selectedUser.name || 
                        selectedUser.fullName || 
                        `${selectedUser.firstName} ${selectedUser.lastName}`;
        showInfo(`Now viewing tasks as ${userName}`);
      }
    }
  };

  // Format options for MagicSelect
  const options = [
    { 
      value: 'default', 
      label: 'My Tasks (Default)',
      icon: <ViewIcon />
    },
    ...viewableUsers.map(user => ({
      value: user._id,
      label: user.name || user.fullName || `${user.firstName} ${user.lastName}`,
      sublabel: user.jobTitle || user.email,
      avatar: user.photo,
      icon: canManage && currentViewAsUser === user._id ? <EditIcon fontSize="small" /> : null
    }))
  ];

  return (
    <Box sx={{ 
      minWidth: { xs: '100%', md: 200 },
      maxWidth: { xs: '100%', md: 260 },
      ...sx 
    }}>
      <MagicSelect
        label="View As"
        value={currentViewAsUser || 'default'}
        onChange={handleViewAsChange}
        options={options}
        size="small"
        disabled={loading}
        startIcon={<ViewIcon />}
        helperText={
          currentViewAsUser 
            ? (canManage ? 'Edit mode - You can reorder tasks' : 'View-only mode')
            : null
        }
      />
    </Box>
  );
};

export default ViewAsSelector;